import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { CALENDAR_CONNECTION_STATUS } from '@/api/calendarSync/calendarSyncApi.types';
import { Icon } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import { MODULE_KEY } from '@/constants/modules';
import {
  useCalendarConnectionQuery,
  useStartCalendarAuthorizationMutation,
} from '@/hooks/useCalendarSync';
import { useHasWorkspaceModule } from '@/hooks/useHasWorkspaceModule';
import { showErrorNotification } from '@/utils/notifications';

export function CalendarReauthorizationBanner() {
  const { t } = useTranslation('settings');
  const hasCalendarSyncModule = useHasWorkspaceModule(MODULE_KEY.calendarSync);
  const connectionQuery = useCalendarConnectionQuery();
  const startAuthorization = useStartCalendarAuthorizationMutation();
  const [isDismissed, setIsDismissed] = useState(false);

  const connection = connectionQuery.data;
  const needsReauthorization =
    connection !== undefined &&
    connection.status === CALENDAR_CONNECTION_STATUS.reauthorizationRequired;

  if (hasCalendarSyncModule === false || needsReauthorization === false || isDismissed === true) {
    return null;
  }

  function reconnect() {
    startAuthorization.mutate(undefined, {
      onSuccess: function redirectToGoogle(authorizationUrl) {
        window.location.assign(authorizationUrl);
      },
      onError: function reportError(error) {
        const message = resolveApiErrorMessage(error);
        showErrorNotification(message.length > 0 ? message : t('calendarSync.connectError'));
      },
    });
  }

  return (
    <div className="bg-zoowarningbg border-b border-zoowarningtext/30 px-4 py-2.5">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-zoowarningtext shrink-0">
          <Icon name="AlertCircle" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-zoowarningtext">
            {t('calendarSync.reauthorize.title')}
          </p>
          <p className="text-[12px] text-zoowarningtext/80">
            {t('calendarSync.reauthorize.description')}
          </p>
        </div>
        <Button
          size="chip"
          className="shrink-0"
          isLoading={startAuthorization.isPending}
          onClick={reconnect}
        >
          {startAuthorization.isPending ? null : <Icon name="RefreshCw" />}
          {startAuthorization.isPending
            ? t('calendarSync.connecting')
            : t('calendarSync.reauthorize.action')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t('calendarSync.reauthorize.dismiss')}
          className="shrink-0 text-zoowarningtext/70 hover:text-zoowarningtext"
          onClick={function dismiss() {
            setIsDismissed(true);
          }}
        >
          <Icon name="X" />
        </Button>
      </div>
    </div>
  );
}
